import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {MutationObserver | null} */
  #overlayObserver = null;

  /** @type {boolean} */
  #queued = false;

  /** @type {WeakSet<Element>} */
  #watchedBanners = new WeakSet();

  /** @type {number | null} */
  #showFrame = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    // s2/24: re-check on scroll / resize, when parity moves the watched element (zb:bar-target) or the mode
    // (zb:bar-sync), and after clicks / keys (dialogs, menus, the cart drawer and the consent banner open that way).
    window.addEventListener('scroll', this.#queueUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#queueUpdate, { signal });
    document.addEventListener('zb:bar-target', this.#watchTarget, { signal });
    document.addEventListener('zb:bar-sync', this.#queueUpdate, { signal });
    document.addEventListener('click', this.#queueUpdate, { capture: true, signal });
    document.addEventListener('keyup', this.#queueUpdate, { capture: true, signal });
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#overlayObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * s2/24 (B22, plan §2.4): the bar watches one element, [data-zb-bar-target] (the add button in "buy" mode, the
   * Notify block in "notify" mode; set by zerbay-product-parity.js), and the footer. Every observer only queues a
   * check; #update decides.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (productForm) this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    this.#buyButtonsIntersectionObserver = new IntersectionObserver(this.#queueUpdate);
    this.#mainBottomObserver = new IntersectionObserver(this.#queueUpdate, { rootMargin: '200px 0px 0px 0px' });
    const footer = this.#getFooter();
    if (footer) this.#mainBottomObserver.observe(footer);
    this.#watchTarget();

    this.#overlayObserver = new MutationObserver(this.#queueUpdate);
    this.#overlayObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    this.#overlayObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'open', 'hidden', 'data-zb-open', 'data-zb-panel-open'],
    });
    this.#overlayObserver.observe(this, { attributes: true, attributeFilter: ['data-zb-sticky-mode', 'data-zb-can-add-to-cart'] });
  }

  /** Observes the element parity marked with data-zb-bar-target (re-run on zb:bar-target). */
  #watchTarget = () => {
    if (!this.#buyButtonsIntersectionObserver) return;
    this.#buyButtonsIntersectionObserver.disconnect();
    const target = document.querySelector('[data-zb-bar-target]');
    if (target) this.#buyButtonsIntersectionObserver.observe(target);
    this.#queueUpdate();
  };

  #queueUpdate = () => {
    if (this.#queued) return;
    this.#queued = true;
    requestAnimationFrame(() => {
      this.#queued = false;
      this.#update();
    });
  };

  /** The mode the live host carries: buy | notify | none (anything else counts as none). */
  #mode() {
    const mode = this.dataset.zbStickyMode;
    return mode === 'buy' || mode === 'notify' ? mode : 'none';
  }

  /**
   * Shown only while the mode is not "none", the watched element is rendered and entirely off screen (above or
   * below), the footer is not within 200 px and nothing is open over the page.
   */
  #update() {
    const mode = this.#mode();
    this.#applyMode(mode);
    const target = mode === 'none' ? null : document.querySelector('[data-zb-bar-target]');
    const show = Boolean(target) && this.#isOffScreen(target) && !this.#nearFooter() && !this.#overlayOpen();
    // The second line comes from the fetched section; until it matches the form's variant it is not shown (no stale text).
    const line = this.querySelector('.zb-bar-line');
    const hiddenId = this.#getProductForm()?.querySelector('input[name="id"]');
    const fresh = !hiddenId || !this.dataset.zbBarVariant || hiddenId.value === this.dataset.zbBarVariant;
    if (line) line.classList.toggle('zb-bar-line--stale', !fresh);
    if (show) this.#showStickyBar();
    else this.#hideStickyBar();
  }

  /** Button label and state per mode (labels from data attributes printed from existing keys). */
  #applyMode(mode) {
    const button = this.refs.addToCartButton;
    if (!button) return;
    const disabled = mode === 'none';
    if (button.disabled !== disabled) button.disabled = disabled;
    const label = mode === 'notify' ? this.dataset.zbNotifyLabel : mode === 'buy' ? this.dataset.zbBuyLabel : '';
    const text = button.querySelector('.add-to-cart-text__content > span');
    if (label && text && text.textContent !== label) text.textContent = label;
  }

  /** @param {Element} el */
  #isOffScreen(el) {
    if (!el.getClientRects().length) return false;
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.bottom <= 0 || rect.top >= viewportHeight;
  }

  #getFooter() {
    // the footer section group first (a <footer> element could sit inside page content); in themes migrated from 2.0
    // the footer element doesn't exist
    return (
      document.querySelector('.shopify-section-group-footer-group') ??
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]')
    );
  }

  #nearFooter() {
    const footer = this.#getFooter();
    if (!footer || !footer.getClientRects().length) return false;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return footer.getBoundingClientRect().top < viewportHeight + 200;
  }

  /**
   * Any open dialog (cart drawer, quick add, search, menu sheets, size guide), the header panel, a scroll lock or a
   * visible consent banner (Shopify's privacy banner; never clicked here).
   */
  #overlayOpen() {
    const root = document.documentElement;
    if (root.classList.contains('zb-scroll-lock') || root.classList.contains('zb-sg-lock')) return true;
    if (document.body.classList.contains('zb-mobile-menu-open')) return true;
    if (document.querySelector('dialog[open], details[open] > .menu-drawer, [data-zb-panel-open], [data-zb-open]')) return true;
    const banners = document.querySelectorAll('#shopify-pc__banner, .shopify-pc__banner__dialog');
    for (const banner of banners) {
      // a banner can appear or go by an inline style / class change alone: watch each one found
      if (this.#overlayObserver && !this.#watchedBanners.has(banner)) {
        this.#watchedBanners.add(banner);
        this.#overlayObserver.observe(banner, { attributes: true, subtree: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
      }
      if (banner instanceof HTMLElement && banner.getClientRects().length && banner.offsetHeight > 0 && getComputedStyle(banner).visibility !== 'hidden') return true;
    }
    return false;
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    const mode = this.#mode();
    // s2/24: in "notify" mode the bar takes the shopper to the Notify block and its e-mail field (nothing is sent).
    if (mode === 'notify') {
      this.#goToNotify();
      return;
    }
    if (mode !== 'buy') return;
    const productForm = this.#getProductForm();
    if (productForm) this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    if (!this.#targetAddToCartButton) return;
    if (
      this.refs.addToCartButton.disabled ||
      this.dataset.variantAvailable !== 'true' ||
      this.dataset.zbCanAddToCart !== 'true'
    ) {
      return;
    }

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    // s2/24b: below 360 px the bar shows no photo, so there is nothing to fly to the cart icon; the button's own
    // "added" animation still runs to its end before the state is reset, as on wider screens.
    if (!this.refs.productImage.getClientRects().length) {
      await onAnimationEnd(this.refs.addToCartButton);
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 800);
      return;
    }

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;
    const variantCanAddToCart = newStickyAddToCart.dataset.zbCanAddToCart;
    const canAddToCart =
      variantCanAddToCart === undefined ? variantAvailable === 'true' : variantCanAddToCart === 'true';

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing. s2/24: the bar's visibility follows the live host's mode (parity
    // writes it after this listener); the add path still needs the fetched variant to be addable.
    const mode = this.#mode();
    this.refs.stickyBar.setAttribute('data-stuck', mode === 'none' ? 'false' : currentStuck);
    this.dataset.variantAvailable = canAddToCart ? 'true' : 'false';
    this.dataset.zbCanAddToCart = canAddToCart ? 'true' : 'false';
    this.#applyMode(mode);
    this.#queueUpdate();

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
      // s2/24: the variant the second line now describes
      this.dataset.zbBarVariant = String(variant.id);
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    if (this.#mode() === 'none') return;

    const { stickyBar } = this.refs;
    if (this.#isStuck) return;
    this.#isStuck = true;
    stickyBar.hidden = false;
    stickyBar.removeAttribute('inert');
    if (this.#showFrame) cancelAnimationFrame(this.#showFrame);
    // one frame with the bar laid out off screen, then data-stuck slides it up
    this.#showFrame = requestAnimationFrame(() => {
      this.#showFrame = requestAnimationFrame(() => {
        this.#showFrame = null;
        if (this.#isStuck) stickyBar.dataset.stuck = 'true';
      });
    });
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    if (this.#showFrame) {
      cancelAnimationFrame(this.#showFrame);
      this.#showFrame = null;
    }
    if (stickyBar.dataset.stuck !== 'false') stickyBar.dataset.stuck = 'false';
    if (!stickyBar.hidden) stickyBar.hidden = true;
    if (!stickyBar.hasAttribute('inert')) stickyBar.setAttribute('inert', '');
  }

  /** Notify mode: scroll the live Notify block into view and focus its e-mail field. */
  #goToNotify() {
    const notice = document.querySelector('.shopify-section [data-zb-stock-notify]');
    if (!(notice instanceof HTMLElement)) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    notice.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    const email = notice.querySelector('.zb-stock-notify__email');
    if (email instanceof HTMLElement) {
      try {
        email.focus({ preventScroll: true });
      } catch (_error) {
        email.focus();
      }
    }
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity (s2/24: only on the add button, mode "buy")
    if (available && this.#mode() === 'buy' && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
